<script setup lang="ts">
  import { type RuleExpression, useField } from 'vee-validate'
  import { ref, computed, type PropType, watch, onMounted, onUnmounted, type Component } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Button } from '@/components/ui/button'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
  } from '@/components/ui/command'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { ChevronsUpDown } from 'lucide-vue-next'

  const { t } = useTranslation({
    en: {
      selectOption: 'Select {label}...',
      search: 'Search {label}...',
      unselectedOption: 'None',
      noResults: 'No results found.'
    },
    de: {
      selectOption: '{label} auswählen...',
      search: '{label} suchen...',
      unselectedOption: 'Keine',
      noResults: 'Keine Ergebnisse gefunden.'
    }
  })

  export interface Option {
    value: string | number | null
    title: string
    icon?: Component
  }

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: [String, Number, null],
      required: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    isFilterable: {
      type: Boolean,
      required: false,
      default: false
    },
    options: {
      type: Array as PropType<Option[]>,
      required: true
    },
    inputAction: {
      type: Function,
      required: false,
      default: (_newVal: string, _oldVal: string) => {},
      validator: (fn) => {
        return typeof fn === 'function' && fn.length === 2
      }
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    selectOptionText: {
      type: String,
      required: false
    },
    allowUnselect: {
      type: Boolean,
      required: false,
      default: false
    },
    unselectedOptionText: {
      type: String,
      required: false
    },
    error: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'validation-state'])

  const touched = ref(false)
  const opened = ref(false)
  const selectFieldRef = ref(null)
  const filterQuery = ref('')

  const {
    value: model,
    errorMessage: internalErrorMessage,
    handleBlur,
    meta
  } = useField(props.name, props.rules, {
    syncVModel: true
  })

  const computedOptions = computed(() => {
    const unselectedOption: Option = {
      value: '',
      title: props.unselectedOptionText || t('unselectedOption')
    }
    return props.allowUnselect ? [unselectedOption, ...props.options] : props.options
  })

  const errorMessage = computed(() => {
    if (props.error) {
      return props.error
    }
    return touched.value ? internalErrorMessage.value : ''
  })

  const selectedOption = computed<Option | null>(() => {
    return computedOptions.value.find((option) => option.value === model.value) ?? null
  })

  const filteredOptions = computed(() => {
    return computedOptions.value.filter(
      (option) => option.title?.toLowerCase().includes(filterQuery.value.toLowerCase()) ?? false
    )
  })

  const handleOptionClick = (option: Option) => {
    filterQuery.value = ''
    model.value = option.value
    opened.value = false
    touched.value = true
    emit('update:modelValue', option.value)
  }

  const handleBlurEvent = (event: FocusEvent) => {
    filterQuery.value = ''
    touched.value = true
    handleBlur(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
    emit('blur', event)
  }

  const handleFocusEvent = (event: FocusEvent) => {
    touched.value = true
    emit('focus', event)
  }

  watch(filterQuery, (newVal, oldVal) => {
    props.inputAction(newVal, oldVal)
  })
</script>

<template>
  <div>
    <label
      v-if="label"
      class="mb-2 block text-sm font-medium"
    >
      {{ label }}
    </label>
    <div
      ref="selectFieldRef"
      class="relative"
    >
      <Popover
        v-model:open="opened"
        class="w-full"
      >
        <PopoverTrigger as-child>
          <Button
            variant="outline"
            role="combobox"
            :aria-expanded="opened"
            :disabled="disabled"
            class="w-full justify-between truncate focus-visible:ring-2"
            :class="[
              errorMessage
                ? '!border-red-500 !ring-red-500'
                : 'border-gray-200 focus-visible:ring-purple-500',
              disabled ? 'cursor-not-allowed bg-[#f9f9f9]' : 'cursor-pointer bg-white'
            ]"
            @blur="handleBlurEvent"
            @focus="handleFocusEvent"
          >
            <slot
              name="button-contents"
              :selected-option="selectedOption"
            >
              <div class="flex items-center gap-2">
                <component
                  :is="selectedOption?.icon"
                  v-if="selectedOption !== null && selectedOption.icon"
                  class="size-4"
                />
                <span
                  :class="{
                    'text-gray-800': selectedOption !== null,
                    'text-gray-500': selectedOption === null
                  }"
                >
                  {{
                    selectedOption
                      ? selectedOption.title
                      : selectOptionText || t('selectOption', { label: label })
                  }}
                </span>
              </div>
            </slot>
            <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-[--radix-popover-trigger-width] p-0">
          <Command
            :search-terms="filterQuery"
            @update:search-term="(value) => (filterQuery = value)"
          >
            <CommandInput
              v-if="isFilterable"
              v-model="filterQuery"
              :class="[
                'my-2 h-8 rounded-lg border-gray-200 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500',
                disabled ? 'cursor-not-allowed bg-[#f9f9f9]' : 'cursor-pointer bg-white'
              ]"
              :placeholder="t('search', { label: label })"
              :disabled="disabled"
            />
            <CommandEmpty>{{ t('noResults') }}</CommandEmpty>
            <CommandList>
              <CommandGroup>
                <CommandItem
                  v-for="(option, index) in filteredOptions"
                  :key="`${option.value}-${index}`"
                  :value="option.title"
                  :disabled="disabled"
                  @select="
                    () => {
                      handleOptionClick(option)
                    }
                  "
                >
                  <slot
                    name="option-contents"
                    :option="option"
                  >
                    <div class="flex items-center gap-2">
                      <component
                        :is="option.icon"
                        v-if="option.icon"
                        class="size-4"
                      />
                      <span
                        :class="{
                          'text-gray-500': option.value === null,
                          'text-gray-800': option.value !== null
                        }"
                      >
                        {{ option.title }}
                      </span>
                    </div>
                  </slot>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
    <p
      v-if="errorMessage"
      class="mt-1 text-xs text-red-600"
      :class="{ 'opacity-50': disabled }"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<style>
  [data-radix-popper-content-wrapper] {
    z-index: 99 !important;
  }
</style>
